import { Box, Typography, Tooltip, IconButton } from "@mui/material";
import PushPinOutlined from "@mui/icons-material/PushPinOutlined";
import PushPin from "@mui/icons-material/PushPin";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

import SidenavCollapse from "../Sidenav/SidenavCollapse";
import SidenavCollapsible from "../Sidenav/SidenavCollapsible";
import SidenavSubItem from "../Sidenav/SidenavSubItem";

// Custom styles for the Sidenav
import SidenavRoot from "../Sidenav/SidenavRoot";

import {
  useMaterialUIController,
  setMiniSidenav,
  setSidenavPinned,
  setTransparentSidenav,
  setWhiteSidenav,
} from "../../context";
import { canAccessRouteEntry } from "../../utils/authorization";
import { MENU_GROUPS, type MenuGroupKey } from "../../routes";

function Sidenav({
  color = "info",
  brand = "",
  brandName: _brandName,
  routes,
  userRoles = [],
  user = null,
  profilesMenuVisible = false,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: any) {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    sidenavPinned,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [isMobile, setIsMobile] = useState(false);

  const resolveRouteName = (route: { name?: string; nameKey?: string }) => {
    if (!route.nameKey) {
      return route.name;
    }
    const translated = t(route.nameKey);
    return translated === route.nameKey ? route.name : translated;
  };

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      const isMobileDevice = window.innerWidth < 992;
      setIsMobile(isMobileDevice);
      
      if (isMobileDevice) {
        setMiniSidenav(dispatch, true);
        setTransparentSidenav(dispatch, false);
        setWhiteSidenav(dispatch, false);
      } else if (sidenavPinned) {
        setMiniSidenav(dispatch, false);
      } else {
        setMiniSidenav(dispatch, true);
      }
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
    // Do not depend on route `location` here — it was resetting mini/hover after every navigation.
  }, [dispatch, sidenavPinned, transparentSidenav, whiteSidenav]);

  const isRouteActive = (key: string) =>
    collapseName === key || collapseName.startsWith(`${key}/`);

  const renderLeaf = (routeEntry: any, nested: boolean) => {
    const { type, icon, title, noCollapse, key, href, hrefTarget, route, name, nameKey } = routeEntry;
    const displayName = resolveRouteName({ name, nameKey });
    const active = isRouteActive(key);
    const Item = nested ? SidenavSubItem : SidenavCollapse;

    if (type === "collapse") {
      if (href) {
        const sameWindow = hrefTarget === "_self";
        return (
          <Link
            href={href}
            key={key}
            target={sameWindow ? undefined : "_blank"}
            rel={sameWindow ? undefined : "noreferrer"}
            sx={{ textDecoration: "none" }}
          >
            <Item name={displayName} icon={icon} active={active} noCollapse={noCollapse} />
          </Link>
        );
      }
      return (
        <NavLink key={key} to={route}>
          <Item name={displayName} icon={icon} active={active} />
        </NavLink>
      );
    }

    if (type === "title") {
      return (
        <Typography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight={400}
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
          sx={(theme: any) => ({
            [theme.breakpoints.up("lg")]: {
              display: miniSidenav ? "none" : "block",
            },
          })}
        >
          {title}
        </Typography>
      );
    }

    if (type === "divider") {
      return (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return null;
  };

  const renderRoutes = () => {
    const menuRoutes = routes.filter(
      (route: any) =>
        route.menu &&
        canAccessRouteEntry(userRoles, route, { profilesMenuVisible, user }),
    );
    const result: any[] = [];
    const emittedGroups = new Set<string>();

    menuRoutes.forEach((routeEntry: any) => {
      const parentKey = routeEntry.parent as MenuGroupKey | undefined;
      if (!parentKey || !MENU_GROUPS[parentKey]) {
        result.push(renderLeaf(routeEntry, false));
        return;
      }

      if (emittedGroups.has(parentKey)) {
        return;
      }
      emittedGroups.add(parentKey);

      const children = menuRoutes.filter((child: any) => child.parent === parentKey);
      if (children.length === 1) {
        result.push(renderLeaf(children[0], false));
        return;
      }

      const group = MENU_GROUPS[parentKey];
      const groupActive = children.some((child: any) => isRouteActive(child.key));
      const groupName = t(group.nameKey);
      result.push(
        <SidenavCollapsible
          key={parentKey}
          name={groupName === group.nameKey ? group.name : groupName}
          icon={group.icon}
          active={groupActive}
          defaultOpen={groupActive}
        >
          {children.map((child: any) => renderLeaf(child, true))}
        </SidenavCollapsible>,
      );
    });

    return result;
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {!miniSidenav && isMobile && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1199,
            cursor: "pointer"
          }}
          onClick={closeSidenav}
        />
      )}
      
      {/* Sidenav - Don't render if miniSidenav is true on mobile devices */}
      {(!isMobile || !miniSidenav) && (
        <SidenavRoot
          {...rest}
          variant="permanent"
          ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              flex: 1,
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              pt={miniSidenav && !isMobile ? 1 : 1.5}
              pb={miniSidenav && !isMobile ? 1 : 1.5}
              px={miniSidenav && !isMobile ? 0.5 : 2}
              position="relative"
              flexShrink={0}
            >
              {miniSidenav && !isMobile ? (
                <Box
                  component={NavLink}
                  to="/dashboard"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: 1, textDecoration: "none" }}
                >
                  <Box
                    component="img"
                    src="/favicon-32x32.png"
                    alt=""
                    sx={{
                      width: 28,
                      height: 28,
                      objectFit: "contain",
                      filter: darkMode ? "brightness(0) invert(1)" : "brightness(0)",
                    }}
                  />
                </Box>
              ) : (
                <>
                  {brand && (
                    <Box
                      component={NavLink}
                      to="/dashboard"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: 1, textDecoration: "none" }}
                    >
                      <Box
                        component="img"
                        src={brand}
                        alt="Brand"
                        sx={{
                          width: "auto",
                          maxWidth: "100%",
                          maxHeight: 48,
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    display={{ xs: "flex", lg: "none" }}
                    alignItems="center"
                    justifyContent="center"
                    position="absolute"
                    top={8}
                    right={8}
                    onClick={closeSidenav}
                    sx={{ cursor: "pointer", width: 44, height: 44 }}
                  >
                    <Typography variant="h6" color="secondary">
                      <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <Divider
              light={darkMode}
              sx={{ mx: miniSidenav && !isMobile ? 0.75 : 1.5, flexShrink: 0 }}
            />
            <List
              dense={!isMobile}
              disablePadding
              sx={{
                flex: 1,
                overflowX: "hidden",
                overflowY: "auto",
                py: miniSidenav && !isMobile ? 0.25 : 0.75,
                px: miniSidenav && !isMobile ? 0 : 1,
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
              }}
            >
              {renderRoutes()}
            </List>
            {!isMobile && (
              <Box
                sx={{
                  mt: "auto",
                  py: miniSidenav ? 0.75 : 1.5,
                  px: 0.5,
                  display: "flex",
                  justifyContent: "center",
                  borderTop: transparentSidenav
                    ? "1px solid transparent"
                    : darkMode
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Tooltip
                  title={sidenavPinned ? t("sidenav.unpin") : t("sidenav.pin")}
                  placement="right"
                >
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-pressed={sidenavPinned}
                    onClick={() => setSidenavPinned(dispatch, !sidenavPinned)}
                    sx={{
                      color: (theme: any) =>
                        darkMode ? theme.palette.common.white : theme.palette.text.primary,
                    }}
                  >
                    {sidenavPinned ? (
                      <PushPin fontSize="small" />
                    ) : (
                      <PushPinOutlined fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </SidenavRoot>
      )}
    </>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string),
  user: PropTypes.object,
  profilesMenuVisible: PropTypes.bool,
};

export default Sidenav;
